<template>
  <div
    class="page-conversations"
    @keydown.esc="selectConversationDialogOpened = false"
  >
    <!-- Dialog: Send Mass Message -->
    <v-dialog
      v-model="sendMassMessageDialog"
      width="600"
      @keydown.esc="sendMassMessageDialog = false"
      persistent
    >
      <v-card block flat>
        <v-layout>
          <v-flex xs12>
            <v-card>
              <v-card-title class="title pb-0">
                Send Mass Email
              </v-card-title>
              <v-card-text>
                <v-alert
                  :value="true"
                  color="info"
                  outline
                  class="mb-2 text-xs-left"
                >
                  <span
                    >You can utilize variables such as {{ userVariables }}</span
                  >
                </v-alert>
                <v-layout column>
                  <div class="text-xs-left darkgrey--text">Clients</div>
                  <v-autocomplete
                    multiple
                    class="default-input"
                    small-chips
                    solo
                    :items="emailList"
                    v-model="selectedUserIds"
                    placeholder="Select clients"
                    item-text="name_with_email"
                    item-value="id"
                  >
                    <template slot="selection" slot-scope="data">
                      <v-chip
                        small
                        v-if="selectedUserIds.indexOf(data.item.id) === 0"
                      >
                        <span>{{ data.item.name_with_email }}</span>
                      </v-chip>
                      <span
                        v-if="selectedUserIds.indexOf(data.item.id) === 1"
                        class="grey--text caption"
                        >(+{{ selectedUserIds.length - 1 }} other{{
                          selectedUserIds.length > 2 ? 's' : ''
                        }})</span
                      >
                    </template>
                  </v-autocomplete>
                  <div class="text-xs-left darkgrey--text">Subject</div>
                  <v-text-field
                    required
                    placeholder="Type a subject line for the email"
                    solo
                    :rules="textRule"
                    v-model="newSubject"
                    class="default-input"
                  />
                  <div class="text-xs-left darkgrey--text">Message</div>
                  <v-textarea
                    placeholder="Type the body text"
                    solo
                    v-model="newMessage"
                    required
                    :rules="textRule"
                    class="default-input"
                  />
                </v-layout>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn
                  class="default-input text-capitalize primary"
                  @click="sendMassMessage()"
                  :disabled="!massMessageSendable"
                >
                  Send
                </v-btn>
                <v-btn
                  outline
                  class="default-input text-capitalize primary--text"
                  @click="sendTestMassMessage()"
                  :disabled="!massMessageSendable"
                >
                  Send test email to myself
                </v-btn>
                <v-btn
                  outline
                  class="default-input text-capitalize error--text"
                  @click="sendMassMessageDialog = false"
                >
                  Cancel
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-flex>
        </v-layout>
      </v-card>
    </v-dialog>

    <!-- Dialog: Select Conversation -->
    <v-dialog v-model="selectConversationDialogOpened" width="600">
      <v-card block flat>
        <v-layout>
          <v-flex xs12>
            <v-card>
              <v-card-title class="title"
                >Select Conversation
                <v-spacer />
                <v-menu
                  transition="slide-y-transition"
                  bottom
                  nudge-bottom="40"
                  class="my-1"
                >
                  <v-btn
                    slot="activator"
                    flat
                    class="white primary--text text-capitalize ma-0"
                  >
                    <v-icon class="mr-1"> mail_outline </v-icon>
                    Mass Email
                  </v-btn>
                  <v-list>
                    <v-list-tile @click="openSendMassMessageDialog('all')">
                      <v-list-tile-title> All Clients </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile @click="openSendMassMessageDialog('assigned')">
                      <v-list-tile-title>
                        Assigned Clients
                      </v-list-tile-title>
                    </v-list-tile>
                    <v-list-tile @click="openSendMassMessageDialog('none')">
                      <v-list-tile-title>
                        Select Clients
                      </v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
                <v-btn
                  small
                  flat
                  fab
                  class="my-0 white error--text text-capitalize"
                  @click="selectConversationDialogOpened = false"
                >
                  <v-icon size="24"> clear </v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text>
                <v-layout column v-if="hasConversations">
                  <div class="text-xs-left">Select Conversation</div>
                  <v-autocomplete
                    hide-details
                    placeholder="Select a conversation"
                    solo
                    v-model="selectedConversationId"
                    :items="allConversations"
                    item-value="id"
                    item-text="client_names"
                    class="default-input mb-4"
                    @change="conversationDialogOpened = true"
                  >
                    <template slot="item" slot-scope="data">
                      <span v-if="data.item.name"
                        >{{ data.item.name }} ({{
                          data.item.client_names.join(', ')
                        }})</span
                      >
                      <span v-else>{{
                        data.item.client_names.join(', ')
                      }}</span>
                    </template>
                  </v-autocomplete>
                </v-layout>
                <v-layout column align-center v-else-if="isLoading">
                  <v-progress-circular
                    indeterminate
                    size="40"
                    color="primary"
                  ></v-progress-circular>
                </v-layout>
                <v-layout v-else-if="!hasConversations">
                  <v-alert outline value="true" type="info">
                    There is no available conversations. Please add a client
                    first.
                  </v-alert>
                </v-layout>
                <v-layout align-center>
                  <v-spacer class="hidden-xs-only" />
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-card>
    </v-dialog>

    <!-- Partial: Aside -->
    <aside class="page-conversations__aside">
      <header class="page-conversations__aside__header">
        <sc-flex :is-between="true" :is-y-centered="true">
          <sc-h4>Messages</sc-h4>
          <sc-button-group>
            <!-- <sc-button
              type="secondary"
              size="small"
              icon-left="archive"
              :is-icon-only="true"
            ></sc-button> -->
            <!-- <sc-button
              type="secondary"
              size="small"
              icon-left="gear"
              :is-icon-only="true"
            ></sc-button> -->
            <sc-button
              @click="toggleCreateDialog()"
              v-if="!selectedCompanyId"
              type="primary"
              size="tiny"
              icon-left="add"
              :is-icon-only="true"
            ></sc-button>
          </sc-button-group>
        </sc-flex>
      </header>
      <div class="page-conversations__aside__content">
        <sc-flex v-if="isLoading" :is-centered="true" :padding="16">
          <v-progress-circular
            indeterminate
            size="24"
            color="primary"
          ></v-progress-circular>
        </sc-flex>
        <sc-flex v-else-if="hasConversations" :is-column="true" :gap="4">
          <sc-chat-item
            v-for="(conversation, i) in filteredConversations"
            :key="i"
            @click="selectConversation(conversation.id)"
            :name="conversation.client_names.join(', ')"
            type="user"
            :unread-count="conversation.unSeenCount"
            :is-active="selectedConversationId === conversation.id"
            :last-message="
              getLastMessage(
                conversation.last_message && conversation.last_message.text,
              )
            "
            :last-message-date="
              conversation.last_message_datetime
                ? moment(conversation.last_message_datetime).fromNow()
                : ''
            "
          ></sc-chat-item>
          <sc-flex
            v-if="selectedCompanyId"
            class="page-conversations__aside__back"
            :is-column="true"
            :gap="12"
            :is-centered="true"
          >
            <sc-hr :is-light="true"></sc-hr>
            <sc-button-group :is-full-width="true" align="center">
              <sc-button
                @click="onViewAllClick"
                type="secondary"
                size="tiny"
                icon-left="arrow-left"
                >View All</sc-button
              >
            </sc-button-group>
          </sc-flex>
        </sc-flex>
      </div>
    </aside>

    <!-- Partial: Chat -->
    <section class="page-conversations__content">
      <ConversationsEmpty
        v-if="isLoading || !hasConversations || !selectedConversationId"
        :has-conversations="hasConversations"
      >
        <sc-icon name="users" size="large"></sc-icon>
        <sc-small
          >Select someone from the list to start a conversation.</sc-small
        >
      </ConversationsEmpty>
      <ConversationBoxNew
        v-else
        :selected-conversation-id="selectedConversationId"
        :last-unread-message-id="lastUnreadMessageId"
        :current-user="currentUser"
        @mark-as-unseen="markAsUnseen"
        @change-company="updateDefaultCompanyAndGoToDashboard"
      />
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import { userVariables } from '@/constants/variables';
import isEmpty from 'lodash/isEmpty';
import ConversationBoxNew from '@/components/ConversationBoxNew';
import ConversationsEmpty from './ConversationsEmpty';
import UtilsMixins from '@/mixins/UtilsMixins';

export default {
  name: 'Conversations',
  components: {
    ConversationBoxNew,
    ConversationsEmpty,
  },
  mixins: [UtilsMixins],
  data() {
    return {
      allConversations: [],
      allConversationsFilter: '',
      conversations: [],
      emailList: [],
      isLoading: true,
      lastUnreadMessageId: null,
      newMessage: '',
      newSubject: '',
      selectConversationDialogOpened: false,
      selectedCompanyConversations: [],
      selectedCompanyId: null,
      selectedConversationId: null,
      selectedUserIds: [],
      sendMassMessageDialog: false,
      textRule: [v => !!v || 'This field is required.'],
      userVariables,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      isFirmCommunicationDisabled: 'isFirmCommunicationDisabled',
      currentUser: 'user',
      defaultCompany: 'defaultCompany',
    }),
    ...mapGetters(['isAdvisor']),
    hasConversations() {
      const selectedConversations = this.selectedCompanyConversations || [];
      const conversations = this.conversations || [];
      const allConversations = [].concat(conversations, selectedConversations);

      return Boolean(allConversations.length > 0);
    },
    filteredConversations() {
      if (!this.selectedCompanyId) return this.conversations;
      if (!this.hasConversations) return [];

      return this.conversations.filter(
        conversation => conversation.id === this.selectedConversationId,
      );
    },

    massMessageSendable() {
      return (
        !isEmpty(this.newMessage) &&
        !isEmpty(this.newSubject) &&
        !isEmpty(this.selectedUserIds)
      );
    },
  },
  methods: {
    moment,
    isEmpty,
    toggleCreateDialog() {
      this.selectConversationDialogOpened = !this
        .selectConversationDialogOpened;
    },
    onViewAllClick() {
      this.selectedCompanyId = null;
      this.$router.push('/messages');
    },

    updateDefaultCompanyAndGoToDashboard(companyId) {
      const company = this.companies.find(c => companyId === c.id);
      this.$store.dispatch('setDefaultCompany', { companyId });
      this.$store.dispatch('resetTiles', {
        hashId: company.hash_id,
        clientName: company.name
          .replaceAll(' ', '-')
          .replaceAll(',', '')
          .replaceAll('#', ''),
      });
      if (this.currentUser.is_advisor) {
        this.$router.push({
          name: 'admin-dashboard',
          params: {
            hash:
              company.name
                .replaceAll(' ', '-')
                .replaceAll(',', '')
                .replaceAll('#', '') +
              '-' +
              company.hash_id,
          },
        });
      } else {
        this.$router.push({
          name: 'dashboard',
          params: {
            hash:
              company.name
                .replaceAll(' ', '-')
                .replaceAll(',', '')
                .replaceAll('#', '') +
              '-' +
              company.hash_id,
          },
        });
      }

      this.conversationDialogOpened = false;
    },
    async sendMassMessage() {
      try {
        const response = await API.advisor.sendMassMessage({
          subject: this.newSubject,
          body: this.newMessage
            .trim()
            .split('\n')
            .join('<br/>'),
          clientIds: this.selectedUserIds,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
          console.log(response.data.error);
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've sent the message",
          });
          this.sendMassMessageDialog = false;
          this.newMessage = '';
          this.newSubject = '';
        }
      } catch (e) {
        console.log(e);
      }
    },
    async openSendMassMessageDialog(clientOption) {
      try {
        const response = await API.advisor.listAllUserEmails();

        this.emailList = response.data;
        this.selectedUserIds = [];

        if (clientOption === 'all') {
          this.selectedUserIds = this.emailList.map(e => e.id);
        }

        if (clientOption === 'assigned') {
          this.selectedUserIds = this.emailList
            .filter(e => e.assigned_advisor_id === this.currentUser.id)
            .map(e => e.id);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.sendMassMessageDialog = true;
        this.newMessage = '';
        this.newSubject = '';
      }
    },
    async sendTestMassMessage() {
      try {
        await API.advisor.sendTestMassMessage({
          subject: this.newSubject,
          body: this.newMessage
            .trim()
            .split('\n')
            .join('<br/>'),
        });

        this.$store.dispatch('application/setSuccessNotification', {
          message:
            "You've sent the test message. You should receive it within couple minutes.",
        });
      } catch (error) {
        console.error(error);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem sending the message.',
        });
      }
    },
    selectConversation(id) {
      this.selectedConversationId = id;
      this.getMyConversations(true);
      this.$store.dispatch('getMyConversations');

      const selectedConversation = this.filteredConversations.find(
        c => c.id === id,
      );
      const selectedConversationHasUnreadByMe = Boolean(
        selectedConversation &&
          selectedConversation.unSeenCount > 0 &&
          selectedConversation.unseen_messages &&
          selectedConversation.unseen_messages.some(message =>
            message.unseen_user_ids.includes(this.currentUser.id),
          ),
      );

      this.lastUnreadMessageId = selectedConversationHasUnreadByMe
        ? selectedConversation.unseen_messages.filter(message =>
            message.unseen_user_ids.includes(this.currentUser.id),
          )[0].id
        : null;
    },
    async markAsUnseen() {
      await API.conversation.markAsUnseen(this.selectedConversationId);
      this.getMyConversations(true);
    },
    getLastMessage(message) {
      if (!message) return '';

      const isAttachment = message.includes('$$attached$$');

      if (isAttachment) return 'Attachment';

      return this.removeHtml(message);
    },
    // Get companyId
    async getEntityConversation() {
      if (!this.selectedCompanyId) return;

      try {
        const { data: currentCompany } = await API.company.getCompany(
          this.selectedCompanyId,
        );
        this.currentCompany = currentCompany;

        // Error: No owner id (@chris to move it inline later)
        if (isEmpty(this.currentCompany.owner_ids)) {
          this.$store.dispatch('application/setErrorNotification', {
            message:
              'There is no active owner for this entity. Please add an email or an additional owner.',
          });

          this.$router.push(
            `/admin-dashboard?companyId=${this.selectedCompanyId}`,
          );
          return;
        }

        this.previousTitle = document.title;
        document.title = this.currentCompany.name;

        const { data } = await API.conversation.getClientConversation(
          this.selectedCompanyId,
        );

        this.selectedCompanyConversations = data;
        this.selectedConversationId = data.id;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    // Maps conversations, attaching unseen count and sorting by latest message
    mapConversations(newConversations) {
      if (!newConversations) return [];

      let conversationsWithUnseenCount = newConversations.map(conversation => {
        const unSeenCount = conversation.unseen_messages.filter(
          message =>
            message &&
            message.unseen_user_ids &&
            message.unseen_user_ids.includes(this.currentUser.id),
        );
        conversation['unSeenCount'] = unSeenCount ? unSeenCount.length : 0;

        return conversation;
      });

      conversationsWithUnseenCount = conversationsWithUnseenCount.sort(
        (a, b) => {
          return (
            new Date(b.last_message_datetime) -
            new Date(a.last_message_datetime)
          );
        },
      );

      return conversationsWithUnseenCount;
    },
    // Get list of conversations for logged in user
    async getMyConversations(isSilent = false) {
      this.isLoading = !isSilent;

      try {
        const { data } = await API.conversation.getMyConversations();

        this.conversations = this.mapConversations(data);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
    // Get the company id from hash or companyId query param
    async getCompanyId() {
      // Passed company.id in params.companyId
      if (this.$route.query.companyId)
        return parseInt(this.$route.query.companyId);

      // Passed Company.id in params.hash
      if (this.$route.params.hash) {
        const { hash } = this.$route.params;
        const hashId = hash.substring(hash.lastIndexOf('-') + 1);
        const { data } = await API.company.getCompanyIdByHashId(hashId);

        return data.id;
      }

      return null;
    },
  },
  async created() {
    if (this.isFirmCommunicationDisabled) return this.$router.push('/');

    this.selectedCompanyId = await this.getCompanyId();

    // Get my conversations
    await this.getMyConversations();

    // Load conversations for selected companyId
    await this.getEntityConversation();
  },
  watch: {
    selectConversationDialogOpened: {
      async handler() {
        const { data } = await API.advisor.getAllConversations();

        this.allConversations = data;
      },
    },
  },
};
</script>

<style lang="css" scoped>
.page-conversations {
  display: flex;
  max-height: calc(100dvh - 180px);

  background-color: var(--gray-0);
  border-radius: 8px;
}

.page-conversations__aside {
  display: flex;
  flex-direction: column;
  min-width: 294px;
  width: 294px;

  border-right: 1px solid var(--gray-100);
}

.page-conversations__aside__back {
  padding-top: 12px;
}

/* NOTE @chris Will be moved to layout in a followup diff. This is to make the content container 100% height. */
.page-conversations__content {
  min-height: calc(100dvh - 180px);
  width: calc(100% - 294px);
}

.page-conversations__content > * {
  height: 100%;
}

.page-conversations__aside__header {
  position: sticky;
  top: 0;

  padding: 15px 20px;

  background-color: var(--gray-0);
  border-bottom: 1px solid var(--gray-100);
}

.page-conversations__aside__content {
  padding: 12px 10px;
  overflow: auto;
}
</style>
